import React from 'react';
import Dropdown from '../Dropdown';
import DropdownSkeleton from '../DropdownSkeleton';
import { TertiaryButton, SecondaryButton, PrimaryButton } from '../ButtonsV2';

/**
 * EntityManager Component
 *
 * This reusable component allows users to manage an entity (e.g., Organization, Facility).
 * It includes:
 *  - A dropdown to select an existing entity
 *  - An "Add New" button to create a new entity
 *  - An "Edit" button to modify the selected entity
 *  - An "Enable/Disable" toggle to change the entity's status
 *
 * Props:
 * - label: string — Label to display (e.g., "Organization", "Facility")
 * - value: string — Currently selected value
 * - options: { label: string, value: string }[] — List of entities to choose from
 * - onSelect: (value: string) => void — Called when a new option is selected
 * - onAddNew: () => void — Called when the "Add New" button is clicked
 * - onEdit: () => void — Called when the "Edit" button is clicked
 * - onToggleStatus: () => void — Called when the status toggle is clicked
 * - status: "enabled" | "disabled" — Current status of the selected entity
 *
 */
const EntityManager = ({
  entity,
  options = [],
  isLoadingOptions = false,
  disableAddNewEntity = false,
  disableActions = false,
  selectedEntity,
  handleSelectEntity,
  handleEditEntity,
  handleAddOEntity,
  disableDropdown = false,
  handleToggleEntityStatus,
  entityStatus,
  handleEntitySearch,
}) => {
  const statusButtonLabel = selectedEntity?.is_active ? 'Disable' : 'Enable';
  const statusButtonColor = selectedEntity?.is_active
    ? '!bg-[#6B7280] !border-0'
    : '!bg-[#10B981] !border-0';

  const onClickChangeStatus = () => {
    handleToggleEntityStatus(selectedEntity.id, entityStatus);
  };

  return (
    <div className="flex-grow min-w-0">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">{entity}</h3>

        {/** Add New Entity */}
        <TertiaryButton onClick={handleAddOEntity} disabled={disableAddNewEntity}>
          <span className="text-2xl">+</span> Add New
        </TertiaryButton>
      </div>

      {/* Organization Dropdown */}
      <div className="flex items-center w-full">
        {isLoadingOptions ? (
          <DropdownSkeleton />
        ) : (
          <Dropdown
            options={options.map((org) => ({ label: org?.name, value: org?.id }))}
            selectedOption={selectedEntity?.name || entity}
            onSelect={handleSelectEntity}
            onSearch={handleEntitySearch}
            text={entity}
            disabled={disableDropdown}
          />
        )}
      </div>
      {/*Edit and Disable/Enable */}
      <div className="flex justify-center mt-[20px]">
        <SecondaryButton
          fullWidth
          customClass="rounded-tr-none rounded-br-none !border-[#CBD5E1] !text-[#1E3A8A]"
          onClick={handleEditEntity}
          disabled={disableActions}
        >
          Edit
        </SecondaryButton>
        <PrimaryButton
          fullWidth
          customClass={`rounded-tl-none rounded-bl-none ${statusButtonColor} border-0`}
          onClick={onClickChangeStatus}
          disabled={disableActions}
        >
          {statusButtonLabel}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default EntityManager;
